import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { childrenApi, ApiError } from '@/lib/children-api'
import { updatesApi } from '@/lib/updates-api'
import { invitationsApi, type ChildInvitation } from '@/lib/invitations-api'
import { AddUpdateForm } from '@/components/updates'
import { TimelineView } from '@/components/timeline'
import { trackEvent, Events } from '@/lib/observability'
import type { Child } from '@/types/child'
import { WEEKDAYS, WEEKDAY_LABELS, parseAttendanceDays, type Weekday } from '@/types/child'
import type { Update } from '@/types/update'

export function ChildDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [child, setChild] = useState<Child | null>(null)
  const [updates, setUpdates] = useState<Update[]>([])
  const [invitations, setInvitations] = useState<ChildInvitation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  // Add parent form state
  const [showAddParent, setShowAddParent] = useState(false)
  const [parentName, setParentName] = useState('')
  const [parentEmail, setParentEmail] = useState('')
  const [parentPhone, setParentPhone] = useState('')
  const [isAddingParent, setIsAddingParent] = useState(false)

  // Invitation form state
  const [showInviteParent, setShowInviteParent] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [isSendingInvite, setIsSendingInvite] = useState(false)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)

  // Add update form state
  const [showAddUpdate, setShowAddUpdate] = useState(false)
  const [editingUpdate, setEditingUpdate] = useState<Update | null>(null)

  // Attendance days editing state
  const [editingAttendance, setEditingAttendance] = useState(false)
  const [attendanceDays, setAttendanceDays] = useState<Weekday[]>([])
  const [isSavingAttendance, setIsSavingAttendance] = useState(false)

  // Photo upload state
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false)

  // Edit profile state
  const [editingProfile, setEditingProfile] = useState(false)
  const [editFirstName, setEditFirstName] = useState('')
  const [editLastName, setEditLastName] = useState('')
  const [editDateOfBirth, setEditDateOfBirth] = useState('')
  const [isSavingProfile, setIsSavingProfile] = useState(false)

  useEffect(() => {
    if (id) {
      loadChild()
      loadUpdates()
      loadInvitations()
    }
  }, [id])

  const loadChild = async () => {
    try {
      setIsLoading(true)
      const response = await childrenApi.get(id!)
      setChild(response.child)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load child')
    } finally {
      setIsLoading(false)
    }
  }

  const loadUpdates = async () => {
    try {
      const response = await updatesApi.list({ childId: id })
      setUpdates(response.updates)
      setHasMore(response.hasMore)
      setNextCursor(response.nextCursor)
    } catch {
      // Silently fail for updates
    }
  }

  const loadMoreUpdates = async () => {
    if (!nextCursor || isLoadingMore) return

    try {
      setIsLoadingMore(true)
      const response = await updatesApi.list({ childId: id, before: nextCursor })
      setUpdates((prev) => [...prev, ...response.updates])
      setHasMore(response.hasMore)
      setNextCursor(response.nextCursor)
    } catch {
      // Silently fail
    } finally {
      setIsLoadingMore(false)
    }
  }

  const loadInvitations = async () => {
    try {
      const response = await invitationsApi.getChildInvitations(id!)
      setInvitations(response.invitations)
    } catch {
      // Silently fail for invitations
    }
  }

  const handleAddParent = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return

    setIsAddingParent(true)
    try {
      const response = await childrenApi.addParent(id, {
        name: parentName,
        email: parentEmail,
        phone: parentPhone || undefined,
      })

      setChild((prev) =>
        prev ? { ...prev, parents: [...prev.parents, response.parent] } : prev
      )
      setShowAddParent(false)
      setParentName('')
      setParentEmail('')
      setParentPhone('')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add parent')
    } finally {
      setIsAddingParent(false)
    }
  }

  const handleRemoveParent = async (parentId: string, parentName: string) => {
    if (!id || !confirm(`Remove ${parentName}?`)) return

    try {
      await childrenApi.removeParent(id, parentId)
      setChild((prev) =>
        prev ? { ...prev, parents: prev.parents.filter((p) => p.id !== parentId) } : prev
      )
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to remove parent')
    }
  }

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return

    setIsSendingInvite(true)
    setError(null)
    setInviteSuccess(null)

    try {
      const response = await invitationsApi.sendInvitation(id, inviteEmail)
      trackEvent(Events.INVITATION_SENT)
      setInvitations((prev) => [
        ...prev,
        {
          id: response.invitation.id,
          parentEmail: response.invitation.parentEmail,
          status: response.invitation.status as ChildInvitation['status'],
          createdAt: new Date().toISOString(),
          acceptedAt: null,
          expiresAt: response.invitation.expiresAt,
        },
      ])
      setShowInviteParent(false)
      setInviteEmail('')

      // Show invite URL in dev for testing
      if (response.inviteUrl) {
        setInviteSuccess(`Invitation sent! Dev URL: ${response.inviteUrl}`)
      } else {
        setInviteSuccess('Invitation sent successfully!')
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to send invitation')
    } finally {
      setIsSendingInvite(false)
    }
  }

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!confirm('Revoke this invitation?')) return

    try {
      await invitationsApi.revokeInvitation(invitationId)
      trackEvent(Events.INVITATION_REVOKED)
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to revoke invitation')
    }
  }

  const handleUpdateSuccess = (update: Update) => {
    if (editingUpdate) {
      // Replace the edited update in the list
      setUpdates((prev) => prev.map((u) => (u.id === update.id ? update : u)))
      setEditingUpdate(null)
    } else {
      // Add new update to the beginning
      setUpdates((prev) => [update, ...prev])
      setShowAddUpdate(false)
    }
  }

  const handleEditUpdate = (update: Update) => {
    setEditingUpdate(update)
    setShowAddUpdate(false)
  }

  const handleDeleteUpdate = async (updateId: string) => {
    if (!confirm('Delete this update?')) return

    try {
      await updatesApi.delete(updateId)
      setUpdates((prev) => prev.filter((u) => u.id !== updateId))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete update')
    }
  }

  const handleEditAttendance = () => {
    if (child) {
      setAttendanceDays(parseAttendanceDays(child.attendanceDays))
      setEditingAttendance(true)
    }
  }

  const toggleAttendanceDay = (day: Weekday) => {
    setAttendanceDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    )
  }

  const handleSaveAttendance = async () => {
    if (!id) return

    setIsSavingAttendance(true)
    setError(null)
    try {
      const response = await childrenApi.update(id, { attendanceDays })
      setChild(response.child)
      setEditingAttendance(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update attendance days')
    } finally {
      setIsSavingAttendance(false)
    }
  }

  const handleEditProfile = () => {
    if (child) {
      setEditFirstName(child.firstName)
      setEditLastName(child.lastName)
      setEditDateOfBirth(child.dateOfBirth || '')
      setEditingProfile(true)
    }
  }

  const handleSaveProfile = async () => {
    if (!id || !editFirstName.trim() || !editLastName.trim()) return

    setIsSavingProfile(true)
    setError(null)
    try {
      const response = await childrenApi.update(id, {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
        dateOfBirth: editDateOfBirth || undefined,
      })
      setChild(response.child)
      setEditingProfile(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update profile')
    } finally {
      setIsSavingProfile(false)
    }
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !id) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    setIsUploadingPhoto(true)
    setError(null)

    try {
      const response = await childrenApi.uploadPhoto(id, file)
      trackEvent(Events.CHILD_PHOTO_UPLOADED)
      setChild((prev) => prev ? { ...prev, photoUrl: response.photoUrl } : prev)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to upload photo')
    } finally {
      setIsUploadingPhoto(false)
      // Reset the file input
      e.target.value = ''
    }
  }

  const handleDeletePhoto = async () => {
    if (!id || !child?.photoUrl || !confirm('Remove profile photo?')) return

    setIsDeletingPhoto(true)
    setError(null)

    try {
      await childrenApi.deletePhoto(id)
      trackEvent(Events.CHILD_PHOTO_DELETED)
      setChild((prev) => prev ? { ...prev, photoUrl: null } : prev)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete photo')
    } finally {
      setIsDeletingPhoto(false)
    }
  }

  const getStatusBadge = (status: ChildInvitation['status']) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
    }
    return (
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    )
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>
  }

  if (!child) {
    return (
      <div className="p-4">
        <p className="text-red-600">{error || 'Child not found'}</p>
        <button onClick={() => navigate('/children')} className="mt-2 text-primary-600">
          Back to children
        </button>
      </div>
    )
  }

  return (
    <div className="p-4">
      <button
        onClick={() => navigate('/children')}
        className="mb-4 text-sm text-gray-500 hover:text-gray-700"
      >
        &larr; Back to children
      </button>

      {/* Profile Header with Photo */}
      <div className="mb-6 flex items-center gap-4">
        {/* Profile Photo */}
        <div className="relative">
          {child.photoUrl ? (
            <img
              src={child.photoUrl}
              alt={`${child.firstName}'s photo`}
              className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center border-2 border-gray-200">
              <span className="text-2xl font-semibold text-primary-600 ph-no-capture">
                {child.firstName[0]}{child.lastName[0]}
              </span>
            </div>
          )}

          {/* Photo Upload/Delete Controls */}
          {!child.isLocked && (
            <div className="absolute -bottom-1 -right-1 flex gap-1">
              <label
                className={`flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-primary-600 text-white shadow hover:bg-primary-700 ${isUploadingPhoto ? 'opacity-50 cursor-wait' : ''}`}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={isUploadingPhoto || isDeletingPhoto}
                  className="sr-only"
                />
                {isUploadingPhoto ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </label>
              {child.photoUrl && (
                <button
                  onClick={handleDeletePhoto}
                  disabled={isUploadingPhoto || isDeletingPhoto}
                  className={`flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600 ${isDeletingPhoto ? 'opacity-50' : ''}`}
                >
                  {isDeletingPhoto ? (
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Name and Age */}
        <div className="flex-1">
          {editingProfile ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  placeholder="First name"
                  className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-lg font-bold focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <input
                  type="text"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  placeholder="Last name"
                  className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-lg font-bold focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date of birth</label>
                <input
                  type="date"
                  value={editDateOfBirth}
                  onChange={(e) => setEditDateOfBirth(e.target.value)}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile || !editFirstName.trim() || !editLastName.trim()}
                  className="rounded bg-primary-600 px-3 py-1 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSavingProfile ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingProfile(false)}
                  disabled={isSavingProfile}
                  className="rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <div>
                <h1 className="text-2xl font-bold ph-no-capture">
                  {child.firstName} {child.lastName}
                </h1>
                {child.dateOfBirth && (
                  <p className="text-sm text-gray-500">
                    Born {new Date(child.dateOfBirth).toLocaleDateString()}
                  </p>
                )}
              </div>
              {!child.isLocked && (
                <button
                  onClick={handleEditProfile}
                  className="mt-1 text-gray-400 hover:text-gray-600"
                  title="Edit profile"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Locked Profile Banner */}
      {child.isLocked && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <p className="font-medium text-amber-800">This profile is locked</p>
          </div>
          <p className="mt-1 text-sm text-amber-700">
            You can view this profile and its history, but cannot add updates or make changes.
            Upgrade your subscription to unlock.
          </p>
          <Link to="/settings" className="mt-2 inline-block text-sm font-medium text-primary-600 hover:underline">
            Manage Subscription
          </Link>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {inviteSuccess && (
        <div className="mb-4 rounded bg-green-100 p-3 text-sm text-green-700 ph-no-capture">
          {inviteSuccess}
        </div>
      )}

      {/* Add Update Section */}
      {!child.isLocked && (
        <section className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-lg font-medium">Add Update</h2>
            {showAddUpdate && (
              <button
                onClick={() => setShowAddUpdate(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            )}
          </div>

          {showAddUpdate ? (
            <div className="rounded border border-gray-200 p-4">
              <AddUpdateForm
                children={[child]}
                preselectedChildIds={[child.id]}
                onSuccess={handleUpdateSuccess}
                onCancel={() => setShowAddUpdate(false)}
              />
            </div>
          ) : (
            <button
              onClick={() => setShowAddUpdate(true)}
              className="w-full rounded border border-dashed border-gray-300 p-4 text-center text-gray-500 hover:border-primary-300 hover:text-primary-600"
            >
              + Add an update for {child.firstName}
            </button>
          )}
        </section>
      )}

      {/* Edit Update Section */}
      {editingUpdate && !child.isLocked && (
        <section className="mb-6">
          <div className="rounded border border-primary-200 bg-primary-50 p-4">
            <h2 className="mb-3 text-lg font-medium">Edit Update</h2>
            <AddUpdateForm
              children={[child]}
              editUpdate={editingUpdate}
              onSuccess={handleUpdateSuccess}
              onCancel={() => setEditingUpdate(null)}
            />
          </div>
        </section>
      )}

      {/* Timeline Section */}
      <section className="mb-6">
        <h2 className="mb-4 text-lg font-medium">Timeline</h2>
        {updates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500">
            <p className="font-medium">No updates yet</p>
            <p className="mt-1 text-sm">Add your first update above</p>
          </div>
        ) : (
          <TimelineView
            updates={updates}
            showChildren={false}
            onEdit={!child.isLocked ? handleEditUpdate : undefined}
            onDelete={!child.isLocked ? handleDeleteUpdate : undefined}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMoreUpdates}
          />
        )}
      </section>

      {/* Attendance Schedule Section */}
      <section className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-medium">Attendance Schedule</h2>
          {!child.isLocked && !editingAttendance && (
            <button
              onClick={handleEditAttendance}
              className="text-sm text-primary-600 hover:underline"
            >
              Edit
            </button>
          )}
        </div>
        <div className="rounded border border-gray-200 p-4">
          {editingAttendance ? (
            <div>
              <p className="mb-3 text-sm text-gray-600">
                Select the days {child.firstName} usually attends:
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {WEEKDAYS.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleAttendanceDay(day)}
                    disabled={isSavingAttendance}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      attendanceDays.includes(day)
                        ? 'bg-primary-100 border-primary-500 text-primary-700'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {WEEKDAY_LABELS[day]}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveAttendance}
                  disabled={isSavingAttendance}
                  className="btn-primary"
                >
                  {isSavingAttendance ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingAttendance(false)}
                  disabled={isSavingAttendance}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(day => {
                  const isActive = parseAttendanceDays(child.attendanceDays).includes(day)
                  return (
                    <span
                      key={day}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {WEEKDAY_LABELS[day]}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Details Section */}
      <section className="mb-6">
        <h2 className="mb-2 text-lg font-medium">Details</h2>
        <div className="rounded border border-gray-200 p-4">
          {child.dateOfBirth && (
            <p className="mb-2">
              <span className="text-gray-500">Date of Birth:</span>{' '}
              {new Date(child.dateOfBirth).toLocaleDateString()}
            </p>
          )}
          {child.notes && (
            <div>
              <span className="text-gray-500">Notes:</span>
              <p className="mt-1 whitespace-pre-wrap ph-no-capture">{child.notes}</p>
            </div>
          )}
          {!child.dateOfBirth && !child.notes && (
            <p className="text-gray-500">No additional details</p>
          )}
        </div>
      </section>

      {/* Parent Invitations Section */}
      <section className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-medium">Parent Access</h2>
          {!showInviteParent && (
            <button
              onClick={() => setShowInviteParent(true)}
              className="text-sm text-primary-600 hover:underline"
            >
              Invite Parent
            </button>
          )}
        </div>

        {showInviteParent && (
          <form onSubmit={handleSendInvitation} className="mb-4 rounded border border-gray-200 p-4">
            <p className="mb-3 text-sm text-gray-600">
              Send an invitation to a parent's email. They will need to create an account and accept the invitation to view {child.firstName}'s updates.
            </p>
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium">Parent Email *</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isSendingInvite}
                placeholder="parent@example.com"
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" disabled={isSendingInvite}>
                {isSendingInvite ? 'Sending...' : 'Send Invitation'}
              </button>
              <button
                type="button"
                onClick={() => setShowInviteParent(false)}
                className="btn-secondary"
                disabled={isSendingInvite}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {invitations.length === 0 ? (
          <div className="rounded border border-dashed border-gray-300 p-4 text-center text-gray-500">
            No parent invitations sent yet
          </div>
        ) : (
          <div className="space-y-2">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between rounded border border-gray-200 p-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium ph-no-capture">{invitation.parentEmail}</p>
                    {getStatusBadge(invitation.status)}
                  </div>
                  <p className="text-xs text-gray-500">
                    Sent: {new Date(invitation.createdAt).toLocaleDateString()}
                    {invitation.acceptedAt && (
                      <> &middot; Accepted: {new Date(invitation.acceptedAt).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
                {invitation.status === 'pending' && (
                  <button
                    onClick={() => handleRevokeInvitation(invitation.id)}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Contact Information Section (existing parents) */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-medium">Contact Information</h2>
          {!showAddParent && (
            <button
              onClick={() => setShowAddParent(true)}
              className="text-sm text-primary-600 hover:underline"
            >
              Add Contact
            </button>
          )}
        </div>
        <p className="mb-3 text-xs text-gray-500">
          Store parent/guardian contact details for your reference. This doesn't grant them access - use invitations above for that.
        </p>

        {showAddParent && (
          <form onSubmit={handleAddParent} className="mb-4 rounded border border-gray-200 p-4">
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium">Name *</label>
              <input
                type="text"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                required
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isAddingParent}
              />
            </div>
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium">Email *</label>
              <input
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                required
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isAddingParent}
              />
            </div>
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium">Phone</label>
              <input
                type="tel"
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isAddingParent}
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" disabled={isAddingParent}>
                {isAddingParent ? 'Adding...' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddParent(false)}
                className="btn-secondary"
                disabled={isAddingParent}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {child.parents.length === 0 ? (
          <div className="rounded border border-dashed border-gray-300 p-4 text-center text-gray-500">
            No contacts added yet
          </div>
        ) : (
          <div className="space-y-2">
            {child.parents.map((parent) => (
              <div
                key={parent.id}
                className="flex items-center justify-between rounded border border-gray-200 p-3"
              >
                <div>
                  <p className="font-medium ph-no-capture">{parent.name}</p>
                  <p className="text-sm text-gray-500 ph-no-capture">{parent.email}</p>
                  {parent.phone && (
                    <p className="text-sm text-gray-500 ph-no-capture">{parent.phone}</p>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveParent(parent.id, parent.name)}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
